export const money = (v, cur = "IDR") =>
  cur === "USD"
    ? "$ " + Number(v || 0).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "Rp " + Math.round(Number(v || 0)).toLocaleString("id-ID");

export const pct = (v) => `${Number(v || 0).toFixed(2)}%`;

export const fdate = (s) =>
  s ? new Date(s).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "-";

export const daysLeft = (s) => Math.ceil((new Date(s) - new Date()) / 86400000);

const toQ = (v, cur, q, rate) => (cur === q ? v : cur === "USD" ? v * rate : v / (rate || 1));

export const unitCostQ = (it, currency, rate) => toQ(Number(it.unit_cost || 0), it.currency || currency, currency, Number(rate || 1));

const pnum = (v, def) => (v === undefined || v === null || v === "" ? def : Number(v));

export const marginDiv = (p) => Math.max(1 - Number(p || 0) / 100, 0.01);

export const ceilUnit = (v, cur) => {
  const step = cur === "USD" ? 0.01 : 1;
  return Math.ceil(+(v / step).toFixed(6)) * step;
};

export const roundStep = (v, cur) => {
  const step = cur === "USD" ? 1 : 1000;
  const r = Math.floor(v / step + 0.5) * step;
  return r || v <= 0 ? r : v;
};

export const groupKey = (it, d) => (it.group || "").trim() || (it.source === "service" && d.merge_service !== false ? "Service, Instalasi & Jasa" : it.source === "support" && d.merge_support !== false ? "Material Pendukung" : "");

function applyGroupPrices(items, d) {
  const prices = d.group_prices || {};
  const sum = {};
  items.forEach((it) => {
    const g = groupKey(it, d);
    if (!g || !it.show_in_quote) return;
    sum[g] = sum[g] || { cost: 0, auto: 0, rows: [] };
    sum[g].cost += it.line_cost; sum[g].auto += it.line_total; sum[g].rows.push(it);
  });
  Object.entries(sum).forEach(([g, s]) => {
    const p = prices[g];
    if (p === undefined || p === null || p === "") return;
    const target = Math.max(Number(p), 0);
    const byCost = (d.group_meta || {})[g]?.cost_only && s.cost;
    s.rows.forEach((it) => {
      const share = byCost ? it.line_cost / s.cost : s.auto ? it.line_total / s.auto : 1 / s.rows.length;
      it.line_total = target * share;
      it.sell_price = it.qty ? it.line_total / Number(it.qty) : 0;
      it.margin_eff = it.sell_price ? (1 - it.landed_unit / it.sell_price) * 100 : 0;
    });
  });
  return Object.fromEntries(Object.entries(sum).map(([g, s]) => [g, { cost: s.cost, auto: s.auto, total: s.rows.reduce((a, r) => a + r.line_total, 0), n: s.rows.length, names: s.rows.map((r) => r.name).filter(Boolean) }]));
}

export function compute(d, keepSell) {
  const rate = Number(d.exchange_rate || 1);
  const m = Number(d.margin_percent || 0);
  const fp = pnum(d.freight_pct, 5);
  const mp = pnum(d.maintenance_pct, 20);
  const kp = pnum(d.import_markup_pct, 50);
  const sm = Number(d.service_margin_pct || 0);
  const [ox, rk, fc] = ["otex_pct", "risk_pct", "fin_pct"].map((k) => Math.max(Number(d[k] || 0), 0));
  let ic = 0, off = 0, lc = 0, ft = 0, mt = 0;
  const items = (d.items || []).map((it) => {
    const qty = Math.max(Number(it.qty || 0), 0);
    const uc = Math.max(unitCostQ(it, d.currency, rate), 0);
    const imp = it.source === "import";
    const disc = Math.min(Math.max(Number(it.disc_percent || 0), 0), 100);
    const net = uc * (1 - disc / 100);
    const fr = imp ? (net * fp) / 100 : 0;
    const mn = imp ? ((net + fr) * mp) / 100 : 0;
    const landed = net + fr + mn;
    const lo = landed;
    const ipm = it.margin_pct === undefined || it.margin_pct === null || it.margin_pct === "" ? null : Number(it.margin_pct);
    const dm = imp ? kp : it.source === "service" ? sm : it.source === "support" ? (d.support_margin_pct === null || d.support_margin_pct === undefined || d.support_margin_pct === "" ? m : Number(d.support_margin_pct)) : m;
    const man = it.manual_price === undefined || it.manual_price === null || it.manual_price === "" ? null : Number(it.manual_price);
    let base = ceilUnit(lo / marginDiv(ipm ?? dm), d.currency);
    const plp = imp ? Math.min(Math.max(Number(it.pricelist_pct || 0), 0), 99) : 0;
    const cdp = imp ? Math.min(Math.max(Number(it.cust_disc_pct || 0), 0), 100) : 0;
    let lst = plp ? ceilUnit(base / marginDiv(plp), d.currency) : base;
    let pl = man ?? (cdp ? ceilUnit(lst * (1 - cdp / 100), d.currency) : lst);
    if (imp && (it.currency || d.currency) === "USD") {
      const fx = d.currency === "IDR" ? rate : 1;
      const bu = Math.ceil(+(lo / fx / marginDiv(ipm ?? dm)).toFixed(6));
      const lu = plp ? Math.ceil(+(bu / marginDiv(plp)).toFixed(6)) : bu;
      base = bu * fx; lst = lu * fx;
      if (fx !== 1) lst = Math.ceil(+(lst / 10000).toFixed(6)) * 10000;
      pl = man ?? (fx !== 1 ? ceilUnit(lst * (1 - cdp / 100), d.currency) : ceilUnit(lu * (1 - cdp / 100), "USD"));
    }
    let sp;
    if (keepSell && it.sell_price !== null && it.sell_price !== undefined && it.sell_price !== "") sp = Math.max(Number(it.sell_price), 0);
    else sp = d.round_price && man === null ? roundStep(pl, d.currency) : pl;
    ic += net * qty; ft += fr * qty; mt += mn * qty; lc += landed * qty;
    const show = it.show_in_quote !== false;
    return { ...it, show_in_quote: show, unit_cost_q: uc, net_unit: net, freight_unit: fr, maintenance_unit: mn, landed_unit: landed, base_price: base, list_price: lst, pricelist: pl, sell_price: sp, overhead_unit: lo - landed, margin_eff: sp ? (1 - lo / sp) * 100 : 0, line_cost: landed * qty, line_total: show ? sp * qty : 0 };
  });
  const groupSummary = applyGroupPrices(items, d);
  off = items.reduce((a, it) => a + it.line_total, 0);
  const [oxa, rka, fca] = [ox, rk, fc].map((p) => (lc * p) / 100);
  const tc = lc + oxa + rka + fca + Number(d.entertain_cost || 0) + Number(d.commission_cost || 0);
  const profit = off - tc;
  const ppnPct = d.ppn_percent === undefined || d.ppn_percent === "" ? 11 : Number(d.ppn_percent);
  const ppn = d.ppn_enabled ? (off * ppnPct) / 100 : 0;
  return {
    items, group_summary: groupSummary, ppn_enabled: !!d.ppn_enabled, ppn_percent: ppnPct, ppn_amount: ppn, grand_total: off + ppn,
    otex_pct: ox, risk_pct: rk, fin_pct: fc, otex_amount: oxa, risk_amount: rka, fin_amount: fca,
    item_cost: ic, freight_total: ft, maintenance_total: mt, total_cost: tc, offer_total: off, profit,
    final_margin: off ? (profit / off) * 100 : 0, markup: tc ? (profit / tc) * 100 : 0,
  };
}

export const uniqOpts = (rows, key, subKey) => {
  const seen = new Set();
  const out = [];
  rows.forEach((r) => {
    const v = (r[key] || "").toString().trim();
    if (v && !seen.has(v.toLowerCase())) {
      seen.add(v.toLowerCase());
      out.push({ label: v, sub: subKey ? r[subKey] : "", data: r });
    }
  });
  return out;
};

export const searchOpts = (rows, keys) => keys.flatMap((k) => uniqOpts(rows, k)).map((o) => ({ ...o, data: undefined }));

export const matchKw = (row, kw, keys) => {
  if (!kw) return true;
  const k = kw.toLowerCase();
  return keys.some((f) => String(row[f] ?? "").toLowerCase().includes(k));
};
